import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, Loader2, Search, Database, LineChart, Brain, 
  Globe, ChevronDown, ChevronUp, BarChart3
} from 'lucide-react';
import { ResearchProgressUpdate, ResearchSource } from '../../types/research';

// Task component that shows a single research task with animated status indicator
const ResearchTask = ({ 
  message, 
  isActive, 
  isComplete, 
  isLast,
  icon: Icon, 
  ticker, 
  dataSize 
}: { 
  message: string; 
  isActive: boolean; 
  isComplete: boolean; 
  isLast: boolean;
  icon: React.FC<any>;
  ticker?: string;
  dataSize?: number;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      className="flex items-center space-x-3 py-1.5"
    >
      {/* Icon with connection line */}
      <div className="relative flex-shrink-0">
        <motion.div
          className={`z-10 relative w-6 h-6 rounded-full flex items-center justify-center ${
            isComplete 
              ? 'text-green-400' 
              : isActive
                ? 'text-blue-400'
                : 'text-gray-500'
          }`}
          animate={
            isComplete 
              ? { scale: [1, 1.2, 1], transition: { duration: 0.4 } }
              : isActive
                ? { rotate: 360, transition: { duration: 1.5, repeat: Infinity, ease: "linear" } }
                : {}
          }
        >
          {isComplete ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <Icon className="w-5 h-5" />
          )}
        </motion.div>
        
        {!isLast && (
          <div className="absolute top-6 bottom-0 left-1/2 w-0.5 h-full -translate-x-1/2 bg-gradient-to-b from-gray-600 to-gray-800/40" />
        )}
      </div>

      {/* Message content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${
          isComplete 
            ? 'text-green-400' 
            : isActive
              ? 'text-blue-300'
              : 'text-gray-400'
        }`}>
          {message}
          {ticker && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded bg-green-500/20 text-green-400">
              {ticker}
            </span>
          )}
          {dataSize && (
            <span className="ml-1.5 text-xs text-gray-500">
              ({dataSize.toFixed(1)}KB)
            </span>
          )}
        </p>
      </div>

      {/* Activity indicator dot */}
      {isActive && !isComplete && (
        <div className="flex-shrink-0">
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="w-2 h-2 rounded-full bg-blue-400"
          />
        </div>
      )}
    </motion.div>
  );
};

// Define research stages
const stages = [
  { id: 'QUERY_ANALYSIS', icon: Brain, label: 'Analyzing Query' },
  { id: 'WEB_DISCOVERY', icon: Globe, label: 'Web Research' },
  { id: 'TICKER_DISCOVERY', icon: Search, label: 'Finding Stocks' },
  { id: 'DATA_GATHERING', icon: Database, label: 'Gathering Data' },
  { id: 'ANALYSIS', icon: LineChart, label: 'Analyzing Results' },
];

interface ResearchProgressProps {
  progress: ResearchProgressUpdate;
  sources: ResearchSource[];
  isComplete?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface Task {
  id: string;
  message: string;
  stage: string;
  isActive: boolean;
  isComplete: boolean;
  icon: React.FC<any>;
  timestamp: number;
  ticker?: string;
  dataSize?: number;
  searchQuery?: string;
}

const ResearchProgress: React.FC<ResearchProgressProps> = ({
  progress,
  sources,
  isComplete = false,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  
  const [previousStage, setPreviousStage] = useState<string | null>(null);
  const [tickerHistory, setTickerHistory] = useState<string[]>([]);
  const [searchQueryHistory, setSearchQueryHistory] = useState<Record<string, boolean>>({});
  const [showCompletionEffect, setShowCompletionEffect] = useState(false);
  const tasksContainerRef = useRef<HTMLDivElement>(null);
  const lastTaskRef = useRef<string | null>(null);
  const searchQueryCooldown = useRef<Record<string, number>>({});
  const maxSearchTasksPerQuery = useRef<Record<string, number>>({});
  const lastGenericSearchTime = useRef<number>(0);
  
  // Track and update research tasks based on progress
  useEffect(() => {
    // Skip if complete
    if (isComplete) return;
    
    // Get current stage info
    const currentStage = stages.find(s => s.id === progress.stage) || stages[0];
    
    // If stage changed, mark previous tasks as complete and add new initial task
    if (previousStage !== progress.stage) {
      // Mark all tasks from previous stage as complete
      if (previousStage) {
        setTasks(prevTasks => 
          prevTasks.map(task => task.stage === previousStage 
            ? { ...task, isActive: false, isComplete: true } 
            : task
          )
        );
      }
      
      // Create new task for the new stage - use actual message if available
      const newTask = {
        id: `${progress.stage}-${Date.now()}`,
        message: progress.message || `${currentStage.label}...`,
        stage: progress.stage,
        isActive: true,
        isComplete: false,
        icon: currentStage.icon,
        timestamp: Date.now()
      };
      
      // Add task and update lastTaskRef
      setTasks(prev => [...prev, newTask]);
      lastTaskRef.current = newTask.id;
      
      // Update previous stage
      setPreviousStage(progress.stage);
      
      // Reset search query history when stage changes to WEB_DISCOVERY
      if (progress.stage === 'WEB_DISCOVERY') {
        setSearchQueryHistory({});
        searchQueryCooldown.current = {};
        maxSearchTasksPerQuery.current = {};
        lastGenericSearchTime.current = 0;
      }
    } 
    // Current stage hasn't changed but there's a new message or significant progress change
    else if (progress.message) {
      // Only update if message changed or if 15% progress increment
      const lastTask = tasks.find(t => t.id === lastTaskRef.current);
      
      if (lastTask && (
        lastTask.message !== progress.message || 
        (progress.details?.tickers && progress.details.tickers.length > 0) ||
        (progress.details?.searchQuery && progress.details.searchQuery !== lastTask.searchQuery)
      )) {
        // Mark previous task as complete
        setTasks(prevTasks => 
          prevTasks.map(task => task.id === lastTaskRef.current 
            ? { ...task, isActive: false, isComplete: true } 
            : task
          )
        );
        
        // Add ticker to ticker history if available
        if (progress.details?.tickers && progress.details.tickers.length > 0) {
          const newTickers = progress.details.tickers.filter(
            ticker => !tickerHistory.includes(ticker)
          );
          
          if (newTickers.length > 0) {
            setTickerHistory(prev => [...prev, ...newTickers]);
            
            // If we have a new ticker, create a ticker-specific task
            const ticker = newTickers[0];
            const tickerTask = {
              id: `ticker-${ticker}-${Date.now()}`,
              message: `Processing market data for`,
              stage: progress.stage,
              isActive: true,
              isComplete: false,
              icon: BarChart3,
              timestamp: Date.now(),
              ticker: ticker
            };
            
            setTasks(prev => [...prev, tickerTask]);
            lastTaskRef.current = tickerTask.id;
            return;
          }
        }
        
        // Create task based on actual search query or action when available
        let taskIcon = currentStage.icon;
        let taskMessage = progress.message;
        let taskTicker: string | undefined = undefined;
        let taskDataSize: number | undefined = undefined;
        let taskSearchQuery: string | undefined = undefined;
        let shouldCreateTask = true;
        
        // Use search query if available
        if (progress.details?.searchQuery && progress.stage === 'WEB_DISCOVERY') {
          const query = progress.details.searchQuery;
          const currentTime = Date.now();
          const MAX_TASKS_PER_QUERY = 2; // Limit repeated tasks for the same query
          const COOLDOWN_PERIOD = 120000; // 2 minutes cooldown between identical queries
          
          // Check if we've shown this query already
          const hasQuery = searchQueryHistory[query] || false;
          const lastUsed = searchQueryCooldown.current[query] || 0;
          const queryTaskCount = maxSearchTasksPerQuery.current[query] || 0;
          
          // Prevent duplicate search tasks by enforcing cooldown and maximum instances
          if (hasQuery && (
              currentTime - lastUsed < COOLDOWN_PERIOD || 
              queryTaskCount >= MAX_TASKS_PER_QUERY
          )) {
            shouldCreateTask = false;
          }
          
          if (shouldCreateTask) {
            taskMessage = `Searching for "${query}"`;
            taskIcon = Search;
            taskSearchQuery = query;
            taskDataSize = Math.floor(Math.random() * 40) + 5; // Random data size between 5-45KB
            
            // Update history and cooldown tracking
            setSearchQueryHistory(prev => ({...prev, [query]: true}));
            searchQueryCooldown.current[query] = currentTime;
            maxSearchTasksPerQuery.current[query] = (maxSearchTasksPerQuery.current[query] || 0) + 1;
          }
        } 
        // Show ticker processing with actual ticker names when available
        else if (progress.stage === 'DATA_GATHERING' && progress.details?.tickers && progress.details.tickers.length > 0) {
          const ticker = progress.details.tickers[0];
          taskTicker = ticker;
          taskMessage = `Retrieving financial data for`;
          taskDataSize = Math.floor(Math.random() * 40) + 10; // Random data size between 10-50KB
          taskIcon = BarChart3;
        }
        // Show analysis with actual intent when available
        else if (progress.stage === 'ANALYSIS' && progress.details?.intent) {
          taskMessage = `Analyzing ${progress.details.intent}`;
          taskIcon = LineChart;
        }
        // Fallback to more dynamic tasks based on stage with some randomness
        else if (progress.stage === 'DATA_GATHERING' && Math.random() > 0.8 && tickerHistory.length > 0) {
          // Random ticker from history
          const randomTicker = tickerHistory[Math.floor(Math.random() * tickerHistory.length)];
          taskTicker = randomTicker;
          taskMessage = `Retrieving financial data for`;
          taskDataSize = Math.floor(Math.random() * 40) + 10; // Random data size between 10-50KB
          taskIcon = BarChart3;
        } else if (progress.stage === 'WEB_DISCOVERY' && Math.random() > 0.9) {
          // Limit the frequency of generic web search tasks
          const currentTime = Date.now();
          const GENERIC_COOLDOWN = 60000; // 1 minute between generic searches
          
          if (currentTime - lastGenericSearchTime.current < GENERIC_COOLDOWN) {
            shouldCreateTask = false;
          } else {
            taskMessage = `Searching financial news sources`;
            taskIcon = Globe;
            taskDataSize = Math.floor(Math.random() * 30) + 20; // Random data size between 20-50KB
            lastGenericSearchTime.current = currentTime;
          }
        } else if (progress.stage === 'ANALYSIS' && tickerHistory.length > 0 && Math.random() > 0.8) {
          // Random ticker from history
          const randomTicker = tickerHistory[Math.floor(Math.random() * tickerHistory.length)];
          taskTicker = randomTicker;
          taskMessage = `Analyzing performance metrics for`;
          taskIcon = LineChart;
        }
        
        if (shouldCreateTask) {
          const newTask = {
            id: `${progress.stage}-${Date.now()}`,
            message: taskMessage,
            stage: progress.stage,
            isActive: true,
            isComplete: false,
            icon: taskIcon,
            timestamp: Date.now(),
            ticker: taskTicker,
            dataSize: taskDataSize,
            searchQuery: taskSearchQuery
          };
          
          setTasks(prev => [...prev, newTask]);
          lastTaskRef.current = newTask.id;
        }
      }
    }
  }, [progress, previousStage, isComplete, tasks, tickerHistory]);
  
  // Add completion task when research is complete
  useEffect(() => {
    if (isComplete && !showCompletionEffect) {
      // Mark all active tasks as complete
      setTasks(prevTasks => 
        prevTasks.map(task => task.isActive 
          ? { ...task, isActive: false, isComplete: true } 
          : task
        )
      );
      
      // Add final completion task
      const completionTask = {
        id: `completion-${Date.now()}`,
        message: `Research complete - Processing results`,
        stage: 'COMPLETED',
        isActive: true,
        isComplete: false,
        icon: CheckCircle,
        timestamp: Date.now()
      };
      
      setTasks(prev => [...prev, completionTask]);
      
      // Show completion effect with slight delay
      setTimeout(() => {
        setShowCompletionEffect(true);
      }, 1500);
    }
  }, [isComplete, showCompletionEffect]);
  
  // Scroll to bottom when new tasks are added
  useEffect(() => {
    if (tasksContainerRef.current) {
      tasksContainerRef.current.scrollTop = tasksContainerRef.current.scrollHeight;
    }
  }, [tasks]);
  
  // If we're in collapsed state, show a minimized version
  if (isCollapsed) {
    return (
      <button
        onClick={onToggleCollapse}
        className="w-full py-2 cursor-pointer flex items-center space-x-3"
      >
        <div className="relative">
          <motion.div
            animate={{ rotate: isComplete ? 0 : 360 }}
            transition={{ duration: 3, repeat: isComplete ? 0 : Infinity, ease: "linear" }}
            className="p-1"
          >
            {isComplete ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <Loader2 className="w-5 h-5 text-blue-400" />
            )}
          </motion.div>
        </div>
          
        <div className="flex-1">
          <p className="text-sm text-blue-300">
            {isComplete ? 'Research Complete' : `Researching (${Math.round(progress.progress)}%)`}
            {tickerHistory.length > 0 && (
              <span className="ml-2 text-gray-400">
                • {tickerHistory.length} stocks discovered
              </span>
            )}
          </p>
          <p className="text-xs text-gray-400">{progress.message}</p>
        </div>
        
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
    );
  }
  
  // Full research progress display
  return (
    <div className="space-y-1 pt-2 pb-4">
      {/* Header with progress and toggle */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 relative">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 20 20">
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="none"
                stroke="rgba(59, 130, 246, 0.2)"
                strokeWidth="2"
              />
              <motion.circle
                cx="10"
                cy="10"
                r="8"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeDasharray="50.27" // 2 * PI * 8 (radius)
                strokeDashoffset={50.27 * (1 - progress.progress / 100)}
                strokeLinecap="round"
              />
            </svg>
            
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-medium text-blue-400">
                {Math.round(progress.progress)}%
              </span>
            </div>
          </div>
          
          <div>
            <p className="text-sm text-white">
              {isComplete ? 'Research Complete' : 'Researching...'}
            </p>
            <p className="text-xs text-gray-400">
              {sources.length} sources • {tickerHistory.length} stocks discovered
            </p>
          </div>
        </div>
        
        <button
          onClick={onToggleCollapse}
          className="p-1 rounded-md hover:bg-gray-800/70 transition-colors"
        >
          <ChevronUp className="w-4 h-4 text-gray-400" />
        </button>
      </div>
      
      {/* Task list with connecting lines */}
      <div ref={tasksContainerRef} className="space-y-0 pl-2">
        <AnimatePresence>
          {tasks.map((task, index) => (
            <ResearchTask
              key={task.id}
              message={task.message}
              isActive={task.isActive}
              isComplete={task.isComplete}
              isLast={index === tasks.length - 1}
              icon={task.icon}
              ticker={task.ticker}
              dataSize={task.dataSize}
            />
          ))}
        </AnimatePresence>
        
        {tasks.length === 0 && (
          <div className="flex items-center py-2">
            <div className="w-6 h-6 relative flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
            </div>
            <span className="ml-3 text-sm text-gray-400">Initializing research...</span>
          </div>
        )}
      </div>
      
      {/* Completion animation */}
      <AnimatePresence>
        {showCompletionEffect && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="pt-4"
          >
            <motion.div 
              className="flex justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ 
                  type: "spring",
                  stiffness: 200,
                  damping: 12,
                  delay: 0.4
                }}
                className="flex items-center space-x-1"
              >
                <motion.div
                  className="w-1.5 h-1.5 bg-blue-400 rounded-full"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.2, repeat: Infinity, repeatType: "loop", delay: 0 }}
                />
                <motion.div
                  className="w-1.5 h-1.5 bg-blue-400 rounded-full"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.2, repeat: Infinity, repeatType: "loop", delay: 0.4 }}
                />
                <motion.div
                  className="w-1.5 h-1.5 bg-blue-400 rounded-full"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.2, repeat: Infinity, repeatType: "loop", delay: 0.8 }}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResearchProgress;
